import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  Grid,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  TransferWithinAStation as TransferIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Warehouse } from '../../../enums/product';
import { WarehouseService } from '../warehouse/warehouseService';

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (toWarehouseId: string, quantity: number) => Promise<void>;
  product: any;
  sourceWarehouse: Warehouse;
}

const TransferDialog: React.FC<TransferDialogProps> = ({
  open,
  onClose,
  onSubmit,
  product,
  sourceWarehouse,
}) => {
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && product) {
      loadWarehouses();
      setMaxQuantity(product.quantity || 0);
      setQuantity(Math.min(10, product.quantity || 0));
    }
  }, [open, product]);

  const loadWarehouses = async () => {
    try {
      const activeWarehouses = await WarehouseService.getActiveWarehouses();
      const otherWarehouses = activeWarehouses.filter((w) => w.id !== sourceWarehouse.id);
      setWarehouses(otherWarehouses);
      if (otherWarehouses.length > 0) {
        setToWarehouseId(otherWarehouses[0].id!);
      }
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError('Failed to load warehouses');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!toWarehouseId || quantity <= 0) {
      setError('Please select a destination warehouse and quantity');
      return;
    }
    if (quantity > maxQuantity) {
      setError(`Cannot transfer more than ${maxQuantity} units`);
      return;
    }
    try {
      setLoading(true);
      setError('');
      await onSubmit(toWarehouseId, quantity);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to transfer stock');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (value: number) => {
    setQuantity(Math.min(Math.max(0, value), maxQuantity));
  };

  const selectedWarehouse = warehouses.find((w) => w.id === toWarehouseId);
  const availableCapacity = selectedWarehouse?.capacity.available || 0;
  const canTransfer = quantity > 0 && quantity <= availableCapacity;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TransferIcon color="primary" />
            <Typography variant="h6">Transfer Stock</Typography>
          </Stack>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 3,
              py: 3,
              minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}
          >
            {product && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Product
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {product.productName}
                  {product.variantName && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({product.variantName})
                    </Typography>
                  )}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Current Stock
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {product.quantity} units
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      SKU
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {product.sku}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Destination Warehouse</InputLabel>
              <Select
                value={toWarehouseId}
                onChange={(e) => setToWarehouseId(e.target.value)}
                label="Destination Warehouse"
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box>
                      <Typography variant="body2">
                        {warehouse.name} ({warehouse.code})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Available: {warehouse.capacity.available}/{warehouse.capacity.total}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Quantity to Transfer: <strong>{quantity} units</strong>
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={quantity}
                  onChange={(_, value) => handleQuantityChange(value as number)}
                  min={0}
                  max={maxQuantity}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">0</Typography>
                <Typography variant="caption" color="text.secondary">{maxQuantity}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleQuantityChange(Math.max(0, quantity - 1))}
                  disabled={quantity <= 0}
                  sx={{ minWidth: 56 }}
                >
                  −1
                </Button>
                <TextField
                  type="number"
                  size="small"
                  value={quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: maxQuantity, style: { textAlign: 'center' } }}
                  sx={{ width: 100 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleQuantityChange(Math.min(maxQuantity, quantity + 1))}
                  disabled={quantity >= maxQuantity}
                  sx={{ minWidth: 56 }}
                >
                  +1
                </Button>
              </Stack>
            </Box>

            {selectedWarehouse && (
              <Alert severity={canTransfer ? 'info' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {canTransfer
                    ? `Destination has ${availableCapacity} units available`
                    : `Insufficient capacity. Available: ${availableCapacity} units`}
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}
          >
            <Button onClick={onClose} disabled={loading} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canTransfer || loading || quantity === 0}
              startIcon={loading ? <CircularProgress size={16} /> : <TransferIcon />}
            >
              {loading ? 'Transferring...' : 'Transfer Stock'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TransferDialog;